import React, { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  Button,
  Box,
  InputText,
  Text,
  Stack,
  Alert,
  AlertTitle,
  AlertDescription,
  Icon,
  Badge,
} from "@gusarov-studio/rubik-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { routesMap } from "../../../router/routesMap";
import { Logo } from "../../../common/components/Logo";
import { CenteredColumnLayout } from "../../../common/components/Layout";
import { HeroBackground } from "../../../common/components/HeroBackground";
import { LoadingIcon } from "../../../common/components/LoadingIcon";
import { AuthFormSchema } from "../schemas/AuthFormSchema";
import { type AuthFormData, type ErrorInfo } from "../types";
import { processRtkError } from "../../../common/utils/processRtkError";
import { supabase } from "../../../supabase";

function LoginWithEmailScreen() {
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<ErrorInfo | null>(null);
  const [searchParams] = useSearchParams();

  const {
    register,
    formState: { errors },
    clearErrors,
    handleSubmit,
  } = useForm<AuthFormData>({
    defaultValues: {
      email: searchParams.get("email") || "",
      password: "",
    },
    reValidateMode: "onBlur",
    resolver: zodResolver(AuthFormSchema),
  });
  const navigate = useNavigate();

  const attemptLogin = useCallback(
    async (formData: AuthFormData) => {
      setIsLoading(true);

      const { email, password } = formData;

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const errorInfo = processRtkError(error);
        setServerError(errorInfo);
        setIsLoading(false);
        return;
      }

      // Fetch user data to check if onboarding is completed
      const { data: user } = await supabase
        .from("users")
        .select("signup_completed")
        .eq("id", data.user.id)
        .single();

      setIsLoading(false);

      // Redirect based on onboarding status
      if (user && !user.signup_completed) {
        navigate(routesMap.onboarding.url());
      } else {
        navigate(routesMap.newFile.url());
      }
    },
    [navigate, setIsLoading, setServerError],
  );

  return (
    <HeroBackground
      className="h-full"
      variant="gray-100"
    >
      <CenteredColumnLayout className="px-2">
        <Stack
          className="mb-6"
          direction="row"
          spacing={6}
          alignItems="center"
          justifyContent="center"
        >
          <Logo />
          <Text
            as="div"
            size={18}
            weight="600"
          >
            LangFast
          </Text>
          <Badge
            className="relative top-[1px]"
            appearance="subtle"
            variant="white"
            size="16"
          >
            Beta
          </Badge>
        </Stack>
        <Box
          padding="32px 24px 24px"
          borderWidth={1}
          variant="primary"
          radius={16}
        >
          <Text
            align="center"
            block
            size="24"
            weight="600"
          >
            Welcome back
          </Text>
          <Text
            className="mt-1.5!"
            align="center"
            block
            size="14"
            color="muted"
          >
            Login using your work email and password
          </Text>

          {serverError && (
            <Box marginTop={16}>
              <Alert
                variant="danger"
                icon={
                  <Icon
                    glyph="info-circle"
                    color="inherit"
                    size="20"
                  />
                }
              >
                <AlertTitle>{serverError.title}</AlertTitle>
                <AlertDescription>{serverError.description}</AlertDescription>
              </Alert>
            </Box>
          )}

          <Box marginTop={16}>
            <form onSubmit={handleSubmit(attemptLogin)}>
              <Stack spacing={4}>
                <Text
                  as="label"
                  htmlFor="email"
                  size="14"
                  block
                  truncate
                >
                  Email
                </Text>

                <InputText
                  id="email"
                  // type="email"
                  invalid={Boolean(errors.email)}
                  placeholder={"e.g. fullname@company.com"}
                  onInput={() => {
                    if (errors.email) clearErrors("email");
                  }}
                  {...register("email")}
                />

                {errors.email && (
                  <Text
                    color="danger"
                    size="14"
                    block
                    truncate
                  >
                    {errors.email.message}
                  </Text>
                )}
              </Stack>

              <Box marginTop={16}>
                <Stack spacing={4}>
                  <Text
                    as="label"
                    htmlFor="password"
                    size="14"
                    truncate
                  >
                    Password
                  </Text>

                  <InputText
                    id="password"
                    type="password"
                    invalid={Boolean(errors.password)}
                    placeholder={"e.g. _KYCD9YjTA"}
                    onInput={() => {
                      if (errors.password) clearErrors("password");
                    }}
                    {...register("password")}
                  />

                  {errors.password && (
                    <Text
                      as="span"
                      color="danger"
                      size="14"
                      block
                      truncate
                    >
                      {errors.password.message}
                    </Text>
                  )}
                </Stack>
              </Box>

              <Box marginTop={24}>
                <Button
                  variant="primary"
                  type="submit"
                  shape="pill"
                  widthMode="fill"
                  size="40"
                  disabled={isLoading}
                >
                  {isLoading && <LoadingIcon size={20} />}
                  Continue with email
                </Button>
              </Box>
            </form>

            <Text
              className="mt-4!"
              size="14"
              align="center"
              color="primary"
              as="div"
            >
              Don’t have an account? <Link to={routesMap.signup.url()}>Sign up</Link>
            </Text>
          </Box>
        </Box>
        <Text
          className="mt-6! mx-auto! w-[240px]"
          size="12"
          align="center"
          color="muted"
          as="div"
        >
          By clicking continue, you agree to our{" "}
          <Link to={routesMap.terms.url()}>Terms of Service</Link> and{" "}
          <Link to={routesMap.privacy.url()}>Privacy Policy</Link>.
        </Text>
      </CenteredColumnLayout>
    </HeroBackground>
  );
}

export { LoginWithEmailScreen };
